import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  IconButton,
  Typography,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Checkbox,
  Tooltip,
  Stack,
  Alert,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
  Badge,
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  Download as DownloadIcon,
  QrCode as QrCodeIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  getInventoryItemsByVariant,
  updateItemStatus,
  deleteInventoryItem,
  bulkDeleteInventoryItems,
  INVENTORY_ITEM_STATUS,
} from '../services/inventoryItemService';
import AddInventoryItemDialog from './AddInventoryItemDialog';
import ConfirmationDialog from '../../../components/ConfirmationDialog';

/**
 * InventoryItemsManagementDialog
 * Comprehensive dialog for managing inventory items (IMEI/Serial tracking) for a product variant
 * 
 * Features:
 * - Filter by status (Available, Sold, Damaged, Returned, Reserved, In Transit)
 * - Search by IMEI/Serial Number with debounce
 * - Inline status change
 * - Bulk operations (delete multiple items)
 * - Add new inventory items
 * - Pagination
 * - Export functionality
 */
const InventoryItemsManagementDialog = ({
  open,
  onClose,
  variantId,
  variantName = 'Variant',
  productName = 'Product',
}) => {
  const { t } = useTranslation();

  // State
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  
  // Selection
  const [selectedItems, setSelectedItems] = useState([]);
  
  // UI State
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [actionMenuItem, setActionMenuItem] = useState(null);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load items when filters change
  useEffect(() => {
    if (open && variantId) {
      console.log('🔵 InventoryItemsManagementDialog opened with variantId:', variantId);
      loadItems();
    } else if (open && !variantId) {
      console.warn('⚠️ Dialog opened but no variantId provided');
    }
  }, [open, variantId, debouncedSearchTerm, selectedStatuses, page, rowsPerPage]);

  // Load inventory items
  const loadItems = async () => {
    if (!variantId) return;

    try {
      setLoading(true);
      const response = await getInventoryItemsByVariant(variantId, {
        searchTerm: debouncedSearchTerm,
        statuses: selectedStatuses,
        pageNumber: page + 1, // API uses 1-based pagination
        pageSize: rowsPerPage,
      });

      console.log('📦 Inventory Items API Response:', response);

      // Handle different response structures
      let itemsArray = [];
      let count = 0;

      if (response?.payload) {
        // Case 1: { payload: { Items: [...], TotalCount: 10 } } - Capital letters (C# API)
        if (response.payload.Items && Array.isArray(response.payload.Items)) {
          itemsArray = response.payload.Items;
          count = response.payload.TotalCount || response.payload.totalCount || itemsArray.length;
        }
        // Case 2: { payload: { items: [...], totalCount: 10 } } - lowercase
        else if (response.payload.items && Array.isArray(response.payload.items)) {
          itemsArray = response.payload.items;
          count = response.payload.totalCount || itemsArray.length;
        }
        // Case 3: { payload: [...] } - direct array in payload
        else if (Array.isArray(response.payload)) {
          itemsArray = response.payload;
          count = itemsArray.length;
        }
        // Case 4: { payload: { data: [...] } } or similar nested structure
        else if (response.payload.data && Array.isArray(response.payload.data)) {
          itemsArray = response.payload.data;
          count = response.payload.totalCount || response.payload.TotalCount || itemsArray.length;
        }
      }
      // Case 5: Direct response { Items: [...], TotalCount: 10 } - Capital letters
      else if (response?.Items && Array.isArray(response.Items)) {
        itemsArray = response.Items;
        count = response.TotalCount || response.totalCount || itemsArray.length;
      }
      // Case 6: Direct response { items: [...], totalCount: 10 } - lowercase
      else if (response?.items && Array.isArray(response.items)) {
        itemsArray = response.items;
        count = response.totalCount || itemsArray.length;
      }
      // Case 7: Direct array response
      else if (Array.isArray(response)) {
        itemsArray = response;
        count = itemsArray.length;
      }

      console.log('📦 Processed Items:', itemsArray.length, 'items, Total Count:', count);
      
      setItems(itemsArray);
      setTotalCount(count);
    } catch (error) {
      console.error('❌ Error loading inventory items:', error);
      console.error('Error details:', error.response?.data || error.message);
      toast.error(error.response?.data?.message || 'Failed to load inventory items');
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Handle search change
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0); // Reset to first page
  };

  // Handle status filter change
  const handleStatusFilterChange = (status) => {
    setSelectedStatuses((prev) => {
      if (prev.includes(status)) {
        return prev.filter((s) => s !== status);
      }
      return [...prev, status];
    });
    setPage(0);
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedStatuses([]);
    setPage(0);
  };

  // Handle status change for single item
  const handleStatusChange = async (itemId, newStatus) => {
    try {
      await updateItemStatus(itemId, newStatus);
      toast.success('Status updated successfully');
      loadItems();
      setActionMenuAnchor(null);
      setActionMenuItem(null);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  // Handle delete single item
  const handleDeleteItem = async (itemId) => {
    try {
      await deleteInventoryItem(itemId);
      toast.success('Item deleted successfully');
      loadItems();
      setSelectedItems((prev) => prev.filter((id) => id !== itemId));
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Failed to delete item');
    } finally {
      setConfirmDelete(null);
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) return;

    try {
      await bulkDeleteInventoryItems(selectedItems);
      toast.success(`${selectedItems.length} item(s) deleted successfully`);
      loadItems();
      setSelectedItems([]);
    } catch (error) {
      console.error('Error bulk deleting items:', error);
      toast.error('Failed to delete items');
    } finally {
      setConfirmDelete(null);
    }
  };

  // Handle select all
  const handleSelectAll = (event) => {
    if (event.target.checked && Array.isArray(items)) {
      setSelectedItems(items.map((item) => item.Id || item.id));
    } else {
      setSelectedItems([]);
    }
  };

  // Handle select single
  const handleSelectItem = (itemId) => {
    setSelectedItems((prev) => {
      if (prev.includes(itemId)) {
        return prev.filter((id) => id !== itemId);
      }
      return [...prev, itemId];
    });
  };

  // Get status chip
  const getStatusChip = (statusValue) => {
    const status = INVENTORY_ITEM_STATUS[statusValue] || { label: 'Unknown', color: 'default' };
    return (
      <Chip
        label={status.label}
        color={status.color}
        size="small"
        sx={{ fontWeight: 600, fontSize: '0.75rem' }}
      />
    );
  };

  // Calculate summary
  const summary = useMemo(() => {
    if (!Array.isArray(items) || items.length === 0) {
      // Return zero counts for all statuses
      const statuses = Object.keys(INVENTORY_ITEM_STATUS);
      const counts = {};
      statuses.forEach((status) => {
        counts[status] = 0;
      });
      return counts;
    }
    
    const statuses = Object.keys(INVENTORY_ITEM_STATUS);
    const counts = {};
    statuses.forEach((status) => {
      counts[status] = items.filter((item) => (item.StatusValue || item.Status) === parseInt(status)).length;
    });
    return counts;
  }, [items]);

  // Handle refresh
  const handleRefresh = () => {
    loadItems();
    toast.info('Refreshed');
  };

  // Handle close
  const handleClose = () => {
    setSearchTerm('');
    setSelectedStatuses([]);
    setSelectedItems([]);
    setPage(0);
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '90vh', display: 'flex', flexDirection: 'column' },
        }}
      >
        {/* Header */}
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid',
            borderColor: 'divider',
            pb: 2,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={600}>
              <QrCodeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Inventory Items - {variantName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {productName} • Manage IMEI/Serial Numbers
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {/* Summary Chips */}
        <Box sx={{ px: 3, pt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {Object.entries(INVENTORY_ITEM_STATUS).map(([key, status]) => (
            <Chip
              key={key}
              label={`${status.label}: ${summary[key] || 0}`}
              color={status.color}
              size="small"
              variant={selectedStatuses.includes(parseInt(key)) ? 'filled' : 'outlined'}
              onClick={() => handleStatusFilterChange(parseInt(key))}
              sx={{ cursor: 'pointer' }}
            />
          ))}
          <Chip
            label={`Total: ${totalCount}`}
            color="primary"
            size="small"
            variant="outlined"
          />
        </Box>

        {/* Filters & Actions */}
        <Box sx={{ px: 3, pt: 2, pb: 1 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            {/* Search */}
            <TextField
              placeholder="Search by IMEI or Serial Number..."
              value={searchTerm}
              onChange={handleSearchChange}
              size="small"
              sx={{ flexGrow: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            {/* Bulk Delete */}
            {selectedItems.length > 0 && (
              <Tooltip title="Delete selected">
                <IconButton
                  color="error"
                  onClick={() => setConfirmDelete('bulk')}
                >
                  <Badge badgeContent={selectedItems.length} color="error">
                    <DeleteIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
            )}

            {/* Clear Filters */}
            {(searchTerm || selectedStatuses.length > 0) && (
              <Button
                size="small"
                onClick={handleClearFilters}
                startIcon={<CloseIcon />}
              >
                Clear
              </Button>
            )}

            {/* Refresh */}
            <Tooltip title="Refresh">
              <IconButton onClick={handleRefresh} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>

            {/* Add New */}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddDialogOpen(true)}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Add Item
            </Button>
          </Stack>
        </Box>

        {/* Table */}
        <DialogContent sx={{ flexGrow: 1, overflow: 'auto', px: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : items.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <QrCodeIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No inventory items found
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                {searchTerm || selectedStatuses.length > 0
                  ? 'Try adjusting your filters'
                  : 'Add inventory items to start tracking'}
              </Typography>
              {(searchTerm || selectedStatuses.length > 0) && (
                <Button onClick={handleClearFilters}>Clear Filters</Button>
              )}
            </Box>
          ) : (
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={
                          selectedItems.length > 0 &&
                          selectedItems.length < items.length
                        }
                        checked={
                          items.length > 0 &&
                          selectedItems.length === items.length
                        }
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell>IMEI</TableCell>
                    <TableCell>Serial Number</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Purchase Date</TableCell>
                    <TableCell align="right">Purchase Cost</TableCell>
                    <TableCell>Sold Date</TableCell>
                    <TableCell>Notes</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => {
                    const itemId = item.Id || item.id;
                    const isSelected = selectedItems.includes(itemId);

                    return (
                      <TableRow
                        key={itemId}
                        hover
                        selected={isSelected}
                        sx={{
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handleSelectItem(itemId)}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {item.IMEI || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {item.SerialNumber || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {getStatusChip(item.StatusValue || item.Status)}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {item.PurchaseDate || item.FormattedPurchaseDate
                              ? new Date(item.PurchaseDate || item.FormattedPurchaseDate).toLocaleDateString()
                              : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {item.PurchasePrice || item.PurchaseCost
                              ? `€${(item.PurchasePrice || item.PurchaseCost).toFixed(2)}`
                              : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {item.SoldDate
                              ? new Date(item.SoldDate).toLocaleDateString()
                              : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            noWrap
                            sx={{ maxWidth: 150 }}
                          >
                            {item.Notes || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              setActionMenuAnchor(e.currentTarget);
                              setActionMenuItem(item);
                            }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        {/* Pagination */}
        {!loading && items.length > 0 && (
          <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </Box>
        )}

        {/* Footer Actions */}
        <DialogActions sx={{ borderTop: '1px solid', borderColor: 'divider', p: 2 }}>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={() => {
          setActionMenuAnchor(null);
          setActionMenuItem(null);
        }}
      >
        <MenuItem disabled>
          <Typography variant="caption" color="text.secondary">
            Change Status
          </Typography>
        </MenuItem>
        <Divider />
        {Object.entries(INVENTORY_ITEM_STATUS).map(([key, status]) => (
          <MenuItem
            key={key}
            onClick={() =>
              handleStatusChange(
                actionMenuItem?.Id || actionMenuItem?.id,
                parseInt(key)
              )
            }
            disabled={actionMenuItem?.Status === parseInt(key)}
          >
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" color={status.color} />
            </ListItemIcon>
            <ListItemText>{status.label}</ListItemText>
          </MenuItem>
        ))}
        <Divider />
        <MenuItem
          onClick={() => {
            setConfirmDelete(actionMenuItem);
            setActionMenuAnchor(null);
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Add Inventory Item Dialog */}
      <AddInventoryItemDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        variantId={variantId}
        variantName={variantName}
        onSuccess={() => {
          loadItems();
          setAddDialogOpen(false);
        }}
      />

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={Boolean(confirmDelete)}
        title={
          confirmDelete === 'bulk'
            ? 'Delete Selected Items'
            : 'Delete Inventory Item'
        }
        message={
          confirmDelete === 'bulk'
            ? `Are you sure you want to delete ${selectedItems.length} selected item(s)? This action cannot be undone.`
            : `Are you sure you want to delete this inventory item? This action cannot be undone.`
        }
        onConfirm={() => {
          if (confirmDelete === 'bulk') {
            handleBulkDelete();
          } else {
            handleDeleteItem(confirmDelete?.Id || confirmDelete?.id);
          }
        }}
        onCancel={() => setConfirmDelete(null)}
      />
    </>
  );
};

export default InventoryItemsManagementDialog;
